/**
 * ArrayItemCard
 * Displays a single array item with drag handle, summary, and actions.
 * Used within ArrayFieldEditor for sortable item lists.
 */

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Pencil, Trash2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import type { FieldConfig } from '@limo/shared'

interface ArrayItemCardProps {
  id: string
  item: Record<string, unknown>
  index: number
  schema: FieldConfig[]
  onEdit: () => void
  onDelete: () => void
  itemLabel?: string
}

export function ArrayItemCard({
  id,
  item,
  index,
  schema,
  onEdit,
  onDelete,
  itemLabel = 'item',
}: ArrayItemCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  // Get primary field for display (prefer title, then first text field)
  const primaryField =
    schema.find((f) => f.key === 'title') ||
    schema.find((f) => f.key === 'label') ||
    schema.find((f) => f.key === 'from') ||
    schema.find((f) => f.key === 'value') ||
    schema.find((f) => f.type === 'text') ||
    schema[0]

  const primaryValue = primaryField
    ? String(item[primaryField.key] || '')
    : ''

  // Get secondary field for subtitle (prefer description, then second field)
  const secondaryField =
    schema.find((f) => f.key === 'description') ||
    schema.find((f) => f.key === 'to') ||
    (schema.length > 1 && schema[1].type === 'text' ? schema[1] : null)

  const secondaryValue = secondaryField
    ? String(item[secondaryField.key] || '')
    : ''

  // For routes, create a combined display
  const isRoute = schema.some((f) => f.key === 'from') && schema.some((f) => f.key === 'to')
  const displayPrimary = isRoute
    ? `${item['from'] || ''} → ${item['to'] || ''}`
    : primaryValue || `${itemLabel} ${index + 1}`

  const displaySecondary = isRoute
    ? `${item['miles'] || ''} • ${item['time'] || ''}`
    : secondaryValue

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={cn(
        'transition-all',
        isDragging && 'opacity-50 shadow-lg z-50'
      )}
    >
      <CardContent className="flex items-center gap-3 py-3 px-3">
        {/* Drag Handle */}
        <button
          className="cursor-grab active:cursor-grabbing touch-none shrink-0"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>

        {/* Item Summary */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">
            {displayPrimary}
          </p>
          {displaySecondary && (
            <p className="text-xs text-muted-foreground truncate">
              {displaySecondary}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onEdit}
          >
            <Pencil className="h-4 w-4" />
            <span className="sr-only">Edit {itemLabel}</span>
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">Delete {itemLabel}</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {itemLabel}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this {itemLabel.toLowerCase()}? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  )
}
